import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

import { useUpdateVatRegulation, useUser } from '~/api/user';
import { Button, Dialog, ExternalLink, Text } from '~/components';
import { Form } from '~/components/Form';
import { toast } from '~/utils/toast';

import type { VatRegulationType } from '../VatRegulationToggle/types';
import type { CompletionTabProps as Props } from './types';

import { formStackStyle } from '../CompleteAccountDialog.css';
import { VatRegulationToggle } from '../VatRegulationToggle/VatRegulationToggle';

export const VatRegulationTab = ({ isLastTab, onCompleted }: Props) => {
  const { data: user } = useUser();
  const mutation = useUpdateVatRegulation();

  const { t } = useTranslation(['settings', 'validation']);

  const { control, handleSubmit } = useForm<{ vatRegulationType: VatRegulationType | null }>({
    defaultValues: {
      vatRegulationType: match(user.vatExempt)
        .returnType<VatRegulationType | null>()
        .with(true, () => 'vatExempt')
        .with(false, () => 'vatNormal')
        .with(null, () => null)
        .exhaustive(),
    },
  });

  const onSubmit = handleSubmit((data) => {
    if (data.vatRegulationType === null) return;

    const vatExempt = data.vatRegulationType === 'vatExempt';

    if (vatExempt === user.vatExempt) return onCompleted();

    mutation.mutate(
      { vatExempt },
      {
        onSuccess: () => {
          toast.success({ description: t('settings:alerts.successUpdated') });
          onCompleted();
        },
        onError: () => toast.danger({ description: t('settings:accountCompletionModal.vatRegulation.error') }),
      },
    );
  });

  return (
    <>
      <Text>
        <Trans components={{ a: <ExternalLink /> }} i18nKey="settings:profile.vatRegulation.help" />
      </Text>

      <Form className={formStackStyle} onSubmit={onSubmit}>
        <Controller
          control={control}
          name="vatRegulationType"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <VatRegulationToggle error={error?.message} onChange={onChange} value={value} />
          )}
          rules={{ required: { message: t('validation:required'), value: true } }}
        />

        <Dialog.Actions>
          <Button loading={mutation.isPending} type="submit">
            {t(`settings:accountCompletionModal.action.${isLastTab ? 'complete' : 'save'}`)}
          </Button>
        </Dialog.Actions>
      </Form>
    </>
  );
};
